import * as React from 'react';
import { Checkbox, CheckboxGroup } from '@components/base/inputs';
import type { CheckboxGroupOption } from '@components/base/inputs';
import { VStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const sizes = ['sm', 'md', 'lg'] as const;

const coverageOptions: CheckboxGroupOption[] = [
  { value: 'liability', label: 'Liability Coverage' },
  { value: 'collision', label: 'Collision Coverage' },
  { value: 'comprehensive', label: 'Comprehensive Coverage' },
  { value: 'uninsured', label: 'Uninsured Motorist' },
];

const agreementOptions: CheckboxGroupOption[] = [
  { value: 'terms', label: 'I agree to the Terms of Service' },
  { value: 'privacy', label: 'I agree to the Privacy Policy' },
  { value: 'marketing', label: 'I would like to receive marketing communications', disabled: true },
];

function CheckboxShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [isChecked, setIsChecked] = React.useState(false);
  const [selectedCoverages, setSelectedCoverages] = React.useState<string[]>(['liability']);
  const [allSelected, setAllSelected] = React.useState(false);
  const [parentIndeterminate, setParentIndeterminate] = React.useState(true);

  // Handle "Select All" logic
  const handleSelectAll = (checked: boolean) => {
    setAllSelected(checked);
    setParentIndeterminate(false);
    if (checked) {
      setSelectedCoverages(coverageOptions.map(o => o.value));
    } else {
      setSelectedCoverages([]);
    }
  };

  // Update parent checkbox state when children change
  React.useEffect(() => {
    if (selectedCoverages.length === 0) {
      setAllSelected(false);
      setParentIndeterminate(false);
    } else if (selectedCoverages.length === coverageOptions.length) {
      setAllSelected(true);
      setParentIndeterminate(false);
    } else {
      setAllSelected(false);
      setParentIndeterminate(true);
    }
  }, [selectedCoverages]);

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="checkbox">
          <Checkbox
            label="I accept the policy terms and conditions"
            size={playgroundProps.size as 'sm' | 'md' | 'lg'}
            disabled={playgroundProps.disabled as boolean}
            indeterminate={playgroundProps.indeterminate as boolean}
            checked={isChecked}
            onChange={(e) => { setIsChecked(e.target.checked); }}
          />
      </ShowcasePreviewSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Checkboxes come in three sizes: small (16px), medium (18px), and large (22px)"
       
      >
          <VStack gap={4}>
            {sizes.map((size) => (
              <Checkbox
                key={size}
                label={`${size.toUpperCase()} Size checkbox`}
                size={size}
              />
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* States */}
      <ShowcaseDemoSection
        title="Checkbox States"
        description="Checkboxes can be unchecked, checked, or indeterminate"
       
      >
          <VStack gap={4}>
            <Checkbox label="Unchecked state" checked={false} onChange={() => {}} />
            <Checkbox label="Checked state" checked={true} onChange={() => {}} />
            <Checkbox label="Indeterminate state" indeterminate />
          </VStack>
      </ShowcaseDemoSection>

      {/* Select All Pattern */}
      <ShowcaseDemoSection
        title="Select All Pattern"
        description="Indeterminate state is useful for 'select all' checkbox patterns"
       
      >
          <VStack gap={3}>
            <Checkbox
              label="Select all coverages"
              checked={allSelected}
              indeterminate={parentIndeterminate}
              onChange={(e) => { handleSelectAll(e.target.checked); }}
            />
            <div className="ml-6 border-l-2 border-gray-200 pl-4">
              <VStack gap={2}>
                {coverageOptions.map((option) => (
                  <Checkbox
                    key={option.value}
                    label={option.label}
                    checked={selectedCoverages.includes(option.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCoverages([...selectedCoverages, option.value]);
                      } else {
                        setSelectedCoverages(selectedCoverages.filter(v => v !== option.value));
                      }
                    }}
                  />
                ))}
              </VStack>
            </div>
          </VStack>
      </ShowcaseDemoSection>

      {/* With Helper Text */}
      <ShowcaseDemoSection
        title="With Helper Text"
        description="Checkboxes can include helper text for additional context"
       
      >
          <VStack gap={4}>
            <Checkbox
              label="Enable automatic renewal"
              helperText="Your policy will automatically renew at the end of the term"
            />
            <Checkbox
              label="Paperless billing"
              helperText="Receive statements via email instead of mail"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Error State */}
      <ShowcaseDemoSection
        title="Error State"
        description="Checkboxes in an error state"
       
      >
          <VStack gap={4}>
            <Checkbox
              label="I agree to the terms"
              error
              helperText="You must agree to continue"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Disabled State */}
      <ShowcaseDemoSection
        title="Disabled State"
        description="Disabled checkboxes cannot be interacted with"
       
      >
          <VStack gap={4}>
            <Checkbox
              label="Unchecked disabled"
              disabled
            />
            <Checkbox
              label="Checked disabled"
              defaultChecked
              disabled
            />
            <Checkbox
              label="Indeterminate disabled"
              indeterminate
              disabled
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Checkbox Group - Vertical */}
      <ShowcaseDemoSection
        title="Checkbox Group (Vertical)"
        description="Group related checkboxes with vertical layout"
       
      >
          <div className="max-w-md">
            <CheckboxGroup
              label="Select Additional Coverages"
              options={coverageOptions}
              value={selectedCoverages}
              onChange={setSelectedCoverages}
              helperText="Choose all coverages that apply to your policy"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Checkbox Group - Horizontal */}
      <ShowcaseDemoSection
        title="Checkbox Group (Horizontal)"
        description="Group related checkboxes with horizontal layout"
       
      >
          <CheckboxGroup
            label="Payment Methods"
            options={[
              { value: 'credit', label: 'Credit Card' },
              { value: 'debit', label: 'Debit Card' },
              { value: 'bank', label: 'Bank Transfer' },
            ]}
            orientation="horizontal"
          />
      </ShowcaseDemoSection>

      {/* Checkbox Group with Required */}
      <ShowcaseDemoSection
        title="Required Checkbox Group"
        description="Checkbox groups that require at least one selection"
       
      >
          <div className="max-w-md">
            <CheckboxGroup
              label="Agreements"
              options={agreementOptions}
              required
              error
              errorMessage="You must agree to the required terms"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Checkbox Group - Disabled */}
      <ShowcaseDemoSection
        title="Disabled Checkbox Group"
        description="Entire group can be disabled"
       
      >
          <div className="max-w-md">
            <CheckboxGroup
              label="Locked Coverages"
              options={coverageOptions}
              value={['liability', 'comprehensive']}
              disabled
              helperText="These coverages are included in your base plan"
            />
          </div>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const CheckboxShowcase: ShowcaseDefinition = {
  id: 'checkbox',
  name: 'Checkbox',
  category: 'inputs',
  description: 'Binary selection control and checkbox groups',
  propsDefinition: [
    {
      name: 'size',
      type: 'select',
      options: ['sm', 'md', 'lg'],
      defaultValue: 'md',
      description: 'Size of the checkbox (sm=16px, md=18px, lg=22px)',
    },
    {
      name: 'disabled',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the checkbox is disabled',
    },
    {
      name: 'indeterminate',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the checkbox is in indeterminate state',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'Native checkbox role',
      'aria-checked (via native checked)',
      'aria-disabled (via native disabled)',
      'aria-invalid (error state)',
      'aria-describedby (helper/error text)',
    ],
    keyboardNav: [
      'Tab: Move focus to/from checkbox',
      'Space: Toggle checked state',
    ],
    notes: [
      'Uses native checkbox with custom styling',
      'Label is associated via wrapping label element',
      'Focus ring visible on keyboard navigation',
      'Indeterminate state set via JavaScript',
    ],
  },
  component: CheckboxShowcaseComponent,
};
